import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  getMetaPublicConfig,
  sendMetaCapiEvent,
  logMetaEvent,
  type MetaPublicConfig,
  type MetaCapiUserData,
  type MetaCapiContent,
} from '@/services/metaPixel';

// =============================================================
// Meta Pixel — proveedor global
// =============================================================
// Carga el script base del Pixel con el ID configurado en el panel, dispara
// `PageView` en cada cambio de ruta y expone helpers (`track`, `trackPurchase`)
// para el resto de la app. La compra también se envía al servidor (CAPI) con el
// MISMO `eventId` para deduplicar.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const PIXEL_SCRIPT_ID = 'meta-pixel-script';
const STANDARD_PIXEL_ID = 'meta-pixel-noscript';

export interface PurchaseInput {
  eventId: string;
  value: number;
  currency?: string;
  contents?: MetaCapiContent[];
  numItems?: number;
  orderId?: string;
  orderNumber?: string;
  userData?: MetaCapiUserData;
}

interface MetaPixelValue {
  enabled: boolean;
  pixelId: string;
  track: (eventName: string, params?: Record<string, unknown>, options?: { eventID?: string }) => void;
  trackPurchase: (input: PurchaseInput) => void;
}

const MetaPixelContext = createContext<MetaPixelValue | undefined>(undefined);

// Inyecta el snippet base oficial del Pixel (una sola vez por sesión).
function injectBasePixel(pixelId: string): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { _fbq?: unknown };
  if (typeof w.fbq === 'function') {
    w.fbq('init', pixelId);
    return;
  }

  const fbq: any = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  w.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;

  if (!document.getElementById(PIXEL_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }

  // Imagen de respaldo (noscript) — inofensiva si el JS sí cargó.
  if (!document.getElementById(STANDARD_PIXEL_ID)) {
    const img = document.createElement('img');
    img.id = STANDARD_PIXEL_ID;
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
  }

  fbq('init', pixelId);
}

export function MetaPixelProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [config, setConfig] = useState<MetaPublicConfig>({
    pixelId: '',
    testEventCode: '',
    enabled: false,
    configured: false,
  });
  const [ready, setReady] = useState(false);

  // Carga la configuración pública y monta el Pixel si está activo.
  useEffect(() => {
    let cancelled = false;
    getMetaPublicConfig().then((cfg) => {
      if (cancelled) return;
      setConfig(cfg);
      if (cfg.configured && cfg.pixelId) {
        injectBasePixel(cfg.pixelId);
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const track = useCallback<MetaPixelValue['track']>((eventName, params, options) => {
    // Registro local para el panel "Embudo en vivo" (best-effort, no bloquea).
    if (config.enabled) {
      const p = (params ?? {}) as Record<string, unknown>;
      void logMetaEvent({
        eventName,
        eventId: options?.eventID,
        value: typeof p.value === 'number' ? p.value : undefined,
        currency: typeof p.currency === 'string' ? p.currency : undefined,
      });
    }
    const fbq = (window as Window).fbq;
    if (typeof fbq !== 'function') return;
    fbq('track', eventName, params ?? {}, options ?? {});
  }, [config.enabled]);

  // PageView en todas las páginas (cada vez que cambia la ruta).
  useEffect(() => {
    if (!ready) return;
    track('PageView');
  }, [ready, pathname, track]);

  const trackPurchase = useCallback<MetaPixelValue['trackPurchase']>(
    (input) => {
      if (!config.enabled || !config.pixelId) return;
      const currency = input.currency ?? 'COP';
      const contents = (input.contents ?? []).map((c) => ({
        id: c.id,
        quantity: c.quantity,
        item_price: c.item_price,
      }));
      const numItems =
        input.numItems ?? contents.reduce((sum, c) => sum + Number(c.quantity ?? 0), 0);

      // 1) Pixel del navegador (deduplicado por eventID).
      track(
        'Purchase',
        {
          value: input.value,
          currency,
          contents,
          num_items: numItems,
          order_id: input.orderNumber,
        },
        { eventID: input.eventId },
      );

      // 2) API de Conversiones (servidor-a-servidor), mismo eventId.
      void sendMetaCapiEvent({
        eventName: 'Purchase',
        eventId: input.eventId,
        orderNumber: input.orderNumber,
        orderId: input.orderId,
        currency,
        value: input.value,
        contents,
        numItems,
        userData: input.userData,
      });
    },
    [config.enabled, config.pixelId, track],
  );

  return (
    <MetaPixelContext.Provider
      value={{ enabled: config.enabled, pixelId: config.pixelId, track, trackPurchase }}
    >
      {children}
    </MetaPixelContext.Provider>
  );
}

export function useMetaPixel(): MetaPixelValue {
  const ctx = useContext(MetaPixelContext);
  if (!ctx) {
    throw new Error('useMetaPixel debe usarse dentro de un MetaPixelProvider');
  }
  return ctx;
}